import {
  File,
  FileImage,
  FileText,
  FileCode,
  Link as LinkIcon,
} from 'lucide-react';
import { Document, DocumentCategory } from '../../lib/api';
import { CATEGORY_COLOR_MAP, FILE_EXTENSIONS } from './constants';

/**
 * Build hierarchical folder options for a {@link SearchableSelect}.
 *
 * Folders are returned in depth-first tree order with each label showing
 * its full path ("Parent › Child"). This stops nested subfolders (e.g. a
 * folder per part number) from flooding a flat, ambiguous list — every
 * row now reads as subordinate to its parent, and because the parent name
 * is part of the label the search box matches on either the parent or the
 * child. Sorting is numeric-aware so "00001 … 00010 … 00311" order
 * naturally. Folders whose parent is missing from the supplied list are
 * treated as roots so nothing silently disappears.
 *
 * @param categories - Flat list of folders (each with optional `parent`).
 * @returns Option list ({ value, label }) ordered parent-before-child.
 */
export function buildFolderOptions(
  categories: DocumentCategory[],
): { value: string; label: string }[] {
  const knownIds = new Set(categories.map((c) => c.id));
  const byParent = new Map<number | null, DocumentCategory[]>();
  for (const c of categories) {
    const key = c.parent != null && knownIds.has(c.parent) ? c.parent : null;
    const siblings = byParent.get(key) ?? [];
    siblings.push(c);
    byParent.set(key, siblings);
  }
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  byParent.forEach((siblings) => {
    siblings.sort((a, b) => collator.compare(a.name, b.name));
  });
  const options: { value: string; label: string }[] = [];
  const walk = (parentId: number | null, prefix: string) => {
    for (const c of byParent.get(parentId) ?? []) {
      const label = prefix ? `${prefix} › ${c.name}` : c.name;
      options.push({ value: c.id.toString(), label });
      walk(c.id, label);
    }
  };
  walk(null, '');
  return options;
}

export function getFileIcon(doc: Document) {
  if (doc.document_type === 'url') {
    return <LinkIcon className="h-5 w-5" />;
  }

  const ext = doc.file_extension?.toLowerCase();

  if (FILE_EXTENSIONS.image.includes(ext || '')) {
    return <FileImage className="h-5 w-5" />;
  }
  if (FILE_EXTENSIONS.pdf.includes(ext || '')) {
    return <File className="h-5 w-5 text-red-500" />;
  }
  if (FILE_EXTENSIONS.spreadsheet.includes(ext || '')) {
    return <File className="h-5 w-5 text-green-500" />;
  }
  if (FILE_EXTENSIONS.archive.includes(ext || '')) {
    return <File className="h-5 w-5 text-yellow-500" />;
  }
  if (FILE_EXTENSIONS.code.includes(ext || '')) {
    return <FileCode className="h-5 w-5" />;
  }

  return <FileText className="h-5 w-5" />;
}

export function getCategoryColor(color: string): string {
  return CATEGORY_COLOR_MAP[color] || CATEGORY_COLOR_MAP['gray'];
}
